import type { FC, ReactNode } from 'react'
import type { LegalSection } from '@/components/generals/legal-document'

import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { LegalDocument } from '@/components/generals/legal-document'

const SECTIONS: LegalSection[] = [
    {
        id: 'agreement',
        title: 'About these terms',
        paragraphs: [
            'These terms govern your use of the Kerning website and of the introductions we make between companies, designers and scouts. By using the site or sending us an application, you agree to them.',
            'Separate written agreements apply once a search or a scouting relationship begins. Where one of those agreements says something different from these terms, the written agreement takes precedence.'
        ]
    },
    {
        id: 'service',
        title: 'What Kerning does',
        paragraphs: [
            'Kerning runs a network of scouts, people respected in the design community, who recommend designers for roles at the companies we work with. We share role briefs with scouts, scouts put designers forward, and designers decide whether they want to be introduced.',
            'We make introductions. We do not employ the designers we introduce, and every hiring decision belongs to the company and the designer.'
        ]
    },
    {
        id: 'companies',
        title: 'For companies',
        paragraphs: [
            'Applying and receiving introductions is free. A placement fee becomes payable only if you hire a designer introduced through Kerning, in any role, within twelve months of the introduction.',
            'The fee, payment terms and any replacement guarantee are set out in the search agreement we agree with you before a search begins.'
        ],
        list: [
            'Please give us accurate information about the role, the team and the terms on offer.',
            'Treat the designers we introduce, and everything they share with you, as confidential.',
            'Let us know when you make an offer to someone we introduced, or when a search is paused or filled.'
        ]
    },
    {
        id: 'designers',
        title: 'For designers',
        paragraphs: [
            'Designers never pay Kerning. Your details are only shared with a hiring team after you have agreed to that specific introduction, and you can decline any introduction without giving a reason.'
        ]
    },
    {
        id: 'scouts',
        title: 'For scouts',
        paragraphs: [
            'Scouts are independent. Being a scout does not make you an employee, agent or partner of Kerning. Your share of placement fees, how it is paid and how you may describe your role are set out in your scout agreement.',
            'Scouts may only put forward designers who have agreed to be introduced, and must keep role briefs and company information confidential.'
        ]
    },
    {
        id: 'confidentiality',
        title: 'Confidentiality',
        paragraphs: [
            'Role briefs, portfolios, introductions and the details of any offer are shared in confidence. Please do not pass them on to anyone outside the people directly involved in the hire.'
        ]
    },
    {
        id: 'acceptable-use',
        title: 'Using the site',
        paragraphs: [
            'Please use the site lawfully and in good faith. You may not copy or scrape its content at scale, interfere with how it works, or send applications on behalf of someone without their permission.'
        ]
    },
    {
        id: 'intellectual-property',
        title: 'Intellectual property',
        paragraphs: [
            'The Kerning name, the site and its content belong to Kerning. The company names and figures shown on the site are illustrative. Designers keep every right in the work they share with us, and we only use it to make the introductions they agree to.'
        ]
    },
    {
        id: 'liability',
        title: 'Liability',
        paragraphs: [
            'We work hard to make good introductions, but we cannot promise that any search will end in a hire or that any hire will work out. The site is provided as it is.',
            'Nothing in these terms limits liability that cannot be limited by law. Otherwise, our total liability to you in connection with the site is limited to the fees you have paid us in the twelve months before the claim.'
        ]
    },
    {
        id: 'changes',
        title: 'Changes and contact',
        paragraphs: [
            'We may update these terms from time to time, and the date at the top of this page always shows the latest version. If a change materially affects an agreement already in place, we will tell you before it applies.',
            'If you have a question about these terms, reply to any email we have sent you and it will reach our team.'
        ]
    }
]

const Terms: FC = (): ReactNode => {
    return (
        <Layout>
            <Seo
                title='Terms of Service'
                description='The terms that govern the Kerning website and the introductions we make between companies, designers and scouts.'
            />

            <LegalDocument
                title='Terms of Service'
                intro='Plain terms for a simple arrangement: scouts recommend, designers choose, companies pay only when they hire.'
                updated='1 October 2026'
                sections={SECTIONS}
            />
        </Layout>
    )
}

export default Terms
